import React, { useEffect, useRef, useState } from 'react';
import { useStore } from 'components/Store';
import EditProductDrawer from 'screens/ProductList/components/EditProductDrawer';
import { NEW_CUSTOM_PRODUCT_DEFAULTS } from 'screens/ProductList/components/EditProductDrawer/customProductDefaults';
import { buildCustomProductPayload } from 'screens/ProductList/helpers/formValuesToPayload';
import useInventoryProduct from 'screens/ProductList/hooks/useInventoryProduct';
import { useToast } from 'components/ToastContainer';
import { Product } from 'screens/Applications&WorkOrders/StepsLayout/Constants/formikData';
import { ItemsToCreatePayload } from 'screens/ProductList/hooks/type';
import { ERROR_TOAST_TIMEOUT } from 'components/ToastContainer/toastManager';

export interface CreateCustomProductDrawerProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: (product: Product) => void;
  zIndex?: number;
  animate?: boolean;
}

const CreateCustomProductDrawer = ({
  visible,
  onClose,
  onSuccess,
  zIndex,
  animate = false
}: CreateCustomProductDrawerProps) => {
  const [retainProduct, setRetainProduct] = useState(visible);
  const wasVisibleRef = useRef(visible);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const newCustomProductRef = useRef({
    ...NEW_CUSTOM_PRODUCT_DEFAULTS,
    isNew: true,
    custom: true,
    formulation: {}
  });
  const { state } = useStore();
  const { addProducts, isUpdateLoading } = useInventoryProduct();
  const toast = useToast();

  useEffect(() => {
    if (visible) {
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
      setRetainProduct(true);
    } else if (animate && wasVisibleRef.current) {
      setRetainProduct(true);
      closeTimeoutRef.current = setTimeout(() => {
        closeTimeoutRef.current = null;
        setRetainProduct(false);
      }, 300);
    }

    wasVisibleRef.current = visible;
  }, [visible, animate]);

  useEffect(
    () => () => {
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    },
    []
  );

  const handleSave = async (values: ItemsToCreatePayload) => {
    const payload = {
      ...buildCustomProductPayload(values),
      organizationId: state?.organization?.id
    };
    const result = await addProducts({
      itemsToCreate: [payload]
    });

    const [, productResponseData] = result || [];
    const { data: createdProduct } = productResponseData || {};

    const product = createdProduct
      ? {
          ...createdProduct,
          averagePricePerUnitUnit: createdProduct.averagePricePerUnitUnit || '',
          stdunit: createdProduct.standardUnit,
          stdfactor: createdProduct.stdFactor,
          stdpackageunit: createdProduct.stdPackageUnit,
          productType: createdProduct.type,
          ratePerAreaUnit: '',
          ratePerTank: 0,
          totalProductUnit: '',
          totalAppliedAcreage: 0,
          targetPest: null,
          applicationMethod: '',
          custom: true,
          isCarrier: false,
          isHidden: false
        }
      : null;

    // Toast logic
    if (product === null) {
      toast.error('Unable to save changes', {
        supportButton: true,
        content:
          'An error prevented your product(s) from being added to your list. Please try again.',
        timeout: ERROR_TOAST_TIMEOUT
      });
    } else {
      onSuccess(product);
    }
  };

  return (
    <EditProductDrawer
      handleEdit={handleSave}
      product={
        visible || (animate && retainProduct)
          ? newCustomProductRef.current
          : undefined
      }
      visible={visible}
      onClose={onClose}
      editProductHeader="Create Custom Product"
      saveText="Create Product"
      isLoading={isUpdateLoading}
      loadingMessage="Saving custom product..."
      suppressAnimation={!animate}
      zIndex={zIndex}
    />
  );
};

export default CreateCustomProductDrawer;
